/**
 * MediaGalleryModal — bilagor (foto/film/dokument) för Moment eller Avvikelse.
 *
 * Prop-kontrakt:
 *   basePath    — t.ex. "/tasks/task_abc" eller "/time-reports/rep_xyz"
 *                 (routen ska erbjuda POST /files, GET /files, DELETE /files/{id},
 *                  POST /files/{id}/preview-token, GET /files/{id}/download)
 *   title       — modal-rubrik ("Bilagor — Räckesmontering")
 *   onClose     — stäng-callback
 *   onChanged   — valfri callback när filer uppladdas/raderas (för räknare)
 *   canDelete   — funktion (file) => boolean, styr om "Radera"-knapp visas per fil
 *   allowCamera — visa "📷 Foto/film"-knappen (kamerainput) på mobil (default: true)
 */
import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadSimple, DownloadSimple, Trash, Paperclip, Eye, FilePdf, FileXls, FileImage, FileVideo, File as FileIcon } from "@phosphor-icons/react";
import ModalPortal from "@/components/ModalPortal";

const ACCEPT_ALL = [
  ".pdf,.xls,.xlsx,.dxf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.mp4,.mov,.webm,.m4v,.3gp",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/*",
  "video/*",
  "application/octet-stream",
].join(",");
const ALLOWED_EXT = [
  "pdf", "xls", "xlsx", "dxf",
  "jpg", "jpeg", "png", "heic", "heif", "webp", "gif",
  "mp4", "mov", "webm", "m4v", "3gp",
];
const MAX_BYTES = 50 * 1024 * 1024;

const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"];
const VIDEO_EXTS = ["mp4", "mov", "webm", "m4v", "3gp"];

function ext(name) {
  return (name.split(".").pop() || "").toLowerCase();
}

function FileTypeIcon({ name }) {
  const e = ext(name);
  if (e === "pdf") return <FilePdf size={22} className="text-[var(--brand-rust)]" />;
  if (e === "xls" || e === "xlsx") return <FileXls size={22} className="text-[var(--brand-forest)]" />;
  if (IMAGE_EXTS.includes(e)) return <FileImage size={22} className="text-blue-600" />;
  if (VIDEO_EXTS.includes(e)) return <FileVideo size={22} className="text-purple-600" />;
  return <FileIcon size={22} className="text-text-muted" />;
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Ladda thumbnail för bild/video-filer (via preview-token).
function useThumbUrl(basePath, file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const e = ext(file.original_name);
    if (!IMAGE_EXTS.includes(e) && !VIDEO_EXTS.includes(e)) return;
    let cancelled = false;
    (async () => {
      try {
        const tokResp = await api.post(`${basePath}/files/${file.file_id}/preview-token`, {});
        const token = tokResp.data?.token;
        if (!token || cancelled) return;
        setUrl(`${API}${basePath}/files/${file.file_id}/download?t=${encodeURIComponent(token)}&inline=1`);
      } catch {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [basePath, file.file_id, file.original_name]);
  return url;
}

function MediaThumb({ basePath, file, onOpen }) {
  const url = useThumbUrl(basePath, file);
  const e = ext(file.original_name);
  const isImg = IMAGE_EXTS.includes(e);
  const isVid = VIDEO_EXTS.includes(e);

  if (!isImg && !isVid) {
    return (
      <button
        onClick={onOpen}
        className="w-14 h-14 rounded-md border border-app bg-app flex items-center justify-center shrink-0 hover:border-brand"
        title="Öppna"
      >
        <FileTypeIcon name={file.original_name} />
      </button>
    );
  }

  return (
    <button
      onClick={onOpen}
      className="w-14 h-14 rounded-md border border-app overflow-hidden bg-black/40 shrink-0 hover:border-brand relative"
      title="Öppna"
    >
      {url ? (
        isImg ? (
          <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <>
            <video src={url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
            <span className="absolute inset-0 flex items-center justify-center text-white text-xl drop-shadow">▶</span>
          </>
        )
      ) : (
        <span className="w-full h-full flex items-center justify-center">
          <FileTypeIcon name={file.original_name} />
        </span>
      )}
    </button>
  );
}

// Fullskärms-preview (bild eller video).
function MediaPreview({ basePath, file, onClose }) {
  const url = useThumbUrl(basePath, file);
  const e = ext(file.original_name);
  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[110] bg-black/85 flex items-center justify-center p-2"
        onClick={onClose}
        data-testid="media-preview-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white/90 hover:text-white p-2 rounded-full bg-black/40"
          aria-label="Stäng"
        >
          <X size={24} />
        </button>
        <div className="max-w-full max-h-full" onClick={(ev) => ev.stopPropagation()}>
          {url && IMAGE_EXTS.includes(e) && (
            <img src={url} alt={file.original_name} className="max-w-[95vw] max-h-[90vh] object-contain" data-testid="image-preview" />
          )}
          {url && VIDEO_EXTS.includes(e) && (
            <video src={url} controls autoPlay playsInline className="max-w-[95vw] max-h-[90vh]" data-testid="video-preview" />
          )}
          {!url && (
            <div className="text-white text-sm">Laddar…</div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}

export default function MediaGalleryModal({
  basePath,
  title,
  onClose,
  onChanged,
  canDelete = () => true,
  allowCamera = true,
}) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`${basePath}/files`);
      setFiles(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte hämta filer");
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  const validateFile = (f) => {
    const e = ext(f.name);
    if (!ALLOWED_EXT.includes(e)) {
      toast.error(`${f.name}: filtypen stöds inte`);
      return false;
    }
    if (f.size > MAX_BYTES) {
      toast.error(`${f.name}: större än 50 MB`);
      return false;
    }
    if (f.size === 0) {
      toast.error(`${f.name}: filen är tom`);
      return false;
    }
    return true;
  };

  const doUpload = async (list) => {
    const arr = Array.from(list).filter(validateFile);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`${basePath}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${arr.length} fil${arr.length === 1 ? "" : "er"} uppladdad${arr.length === 1 ? "" : "e"}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel vid uppladdning");
    } finally {
      setUploading(false);
    }
  };

  const onFileChosen = (ev) => {
    if (ev.target.files?.length) doUpload(ev.target.files);
    ev.target.value = "";
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) doUpload(e.dataTransfer.files);
  };

  const download = async (f) => {
    try {
      const tokResp = await api.post(`${basePath}/files/${f.file_id}/preview-token`, {});
      const token = tokResp.data?.token;
      const url = `${API}${basePath}/files/${f.file_id}/download?t=${encodeURIComponent(token)}`;
      // Anchor-tag med download-attribut → browsern hanterar filnedladdning
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("Kunde inte ladda ner");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Ta bort ${f.original_name}?`)) return;
    try {
      await api.delete(`${basePath}/files/${f.file_id}`);
      toast.success("Borttagen");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel vid borttagning");
    }
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-3 bg-black/60"
        onClick={onClose}
        data-testid="media-modal-backdrop"
      >
        <div
          className="bg-surface rounded-2xl w-full max-w-2xl border border-app overflow-hidden max-h-[92vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="media-gallery-modal"
        >
          <div className="flex items-center justify-between gap-3 p-4 border-b border-app">
            <h2 className="font-heading text-xl flex items-center gap-2">
              <Paperclip size={20} weight="duotone" className="text-brand" />
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary p-1"
              aria-label="Stäng"
              data-testid="media-modal-close"
            >
              <X size={20} />
            </button>
          </div>

          <div
            className="p-4 space-y-3 overflow-auto"
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-text-muted">
                Foto, film & dokument · max 50 MB · flera filer tillåts
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {allowCamera && (
                  <>
                    <label
                      className={`tk-btn-outline inline-flex items-center gap-1.5 cursor-pointer text-xs ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                      data-testid="media-camera-photo-label"
                      title="Ta ett foto med kameran"
                    >
                      📷 Ta foto
                      <input
                        data-testid="media-camera-photo-input"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={onFileChosen}
                        disabled={uploading}
                      />
                    </label>
                    <label
                      className={`tk-btn-outline inline-flex items-center gap-1.5 cursor-pointer text-xs ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                      data-testid="media-camera-video-label"
                      title="Spela in en film med kameran"
                    >
                      🎥 Spela in film
                      <input
                        data-testid="media-camera-video-input"
                        type="file"
                        accept="video/*"
                        capture="environment"
                        className="hidden"
                        onChange={onFileChosen}
                        disabled={uploading}
                      />
                    </label>
                  </>
                )}
                <label
                  className={`tk-btn-primary inline-flex items-center gap-1.5 cursor-pointer text-xs ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="media-upload-label"
                >
                  <UploadSimple size={14} weight="bold" />
                  {uploading ? "Laddar upp…" : "Ladda upp"}
                  <input
                    data-testid="media-upload-input"
                    type="file"
                    accept={ACCEPT_ALL}
                    multiple
                    className="hidden"
                    onChange={onFileChosen}
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>

            <div
              className={`relative border-2 border-dashed rounded-xl transition-colors ${
                dragOver
                  ? "border-[var(--brand-forest)] bg-[var(--brand-forest)]/10"
                  : "border-app"
              }`}
            >
              {dragOver && (
                <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-[var(--brand-forest)]/15 pointer-events-none z-10">
                  <span className="text-sm font-medium text-[var(--brand-forest)] flex items-center gap-2">
                    <UploadSimple size={18} weight="bold" />
                    Släpp filerna här
                  </span>
                </div>
              )}
              <div className="divide-y divide-app min-h-[6rem]">
                {loading ? (
                  <p className="p-6 text-sm text-text-muted text-center">Laddar…</p>
                ) : files.length === 0 ? (
                  <p className="p-8 text-sm text-text-muted text-center" data-testid="media-empty">
                    Inga bilagor ännu. Dra & släpp eller klicka "Ladda upp".
                  </p>
                ) : (
                  files.map((f) => (
                    <div
                      key={f.file_id}
                      className="flex items-center gap-3 p-3"
                      data-testid={`media-row-${f.file_id}`}
                    >
                      <MediaThumb basePath={basePath} file={f} onOpen={() => setPreview(f)} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate" title={f.original_name}>
                          {f.original_name}
                        </div>
                        <div className="text-xs text-text-muted mt-0.5">
                          {formatBytes(f.size)} · {formatDate(f.created_at)}
                          {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => setPreview(f)}
                          className="text-text-muted hover:text-text-primary p-1.5"
                          title="Förhandsvisa"
                          data-testid={`media-preview-${f.file_id}`}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => download(f)}
                          className="text-text-muted hover:text-text-primary p-1.5"
                          title="Ladda ner"
                          data-testid={`media-download-${f.file_id}`}
                        >
                          <DownloadSimple size={16} />
                        </button>
                        {canDelete(f) && (
                          <button
                            onClick={() => remove(f)}
                            className="text-text-muted hover:text-[var(--status-danger)] p-1.5"
                            title="Ta bort"
                            data-testid={`media-delete-${f.file_id}`}
                          >
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {preview && (
        <MediaPreview basePath={basePath} file={preview} onClose={() => setPreview(null)} />
      )}
    </ModalPortal>
  );
}
